import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import supabase from '../supabase/supabase';

const UserRoleContext = createContext();
let providerStartDiagnosticLogged = false;
let providerValueDiagnosticLogged = false;

export const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  CONSULTANT: 'CONSULTANT',
  COMPANY_ADMIN: 'COMPANY_ADMIN',
  DEPARTMENT_LEADER: 'DEPARTMENT_LEADER',
  EMPLOYEE: 'EMPLOYEE'
};

const loadApplicationUser = async (userId) => {
  const { data, error } = await supabase
    .from('users_1715680000000')
    .select('id,email,name,role,status,organization_id')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw new Error('Kunne ikke hente brukerprofilen fra HMS-innsikt.');
  if (!data) throw new Error('Det finnes ingen HMS-innsikt-profil for denne brukeren.');
  return data;
};

const authErrorMessage = (error) => {
  if (error?.message?.toLowerCase().includes('invalid login credentials')) {
    return 'E-post eller passord er feil.';
  }
  if (error?.message?.toLowerCase().includes('email not confirmed')) {
    return 'E-postadressen er ikke bekreftet.';
  }
  return 'Innlogging mislyktes. Kontroller e-post og passord, og prøv igjen.';
};

export const UserRoleProvider = ({ children }) => {
  if (!providerStartDiagnosticLogged) {
    providerStartDiagnosticLogged = true;
    console.info('[AUTH_BOOT_DIAG][UserRoleProvider]', {
      point: 'provider-function-start',
      providerFunctionEntered: true
    });
  }

  // Default til Consultant for demo-formål
  const [role, setRole] = useState(ROLES.CONSULTANT);
  const [session, setSession] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const requestVersion = useRef(0);
  const authUserRef = useRef(null);
  const appUserRef = useRef(null);
  const sessionValidated = useRef(false);

  useEffect(() => {
    let mounted = true;

    const applySession = async (nextSession, event) => {
      const version = ++requestVersion.current;
      const currentAuthUser = authUserRef.current;
      const currentAppUser = appUserRef.current;
      const backgroundRefresh = Boolean(
        event === 'TOKEN_REFRESHED' &&
        sessionValidated.current &&
        currentAuthUser?.id === nextSession?.user?.id &&
        currentAppUser?.id === nextSession?.user?.id &&
        currentAppUser?.status === 'active'
      );

      setSession(nextSession || null);
      setAuthUser(nextSession?.user || null);
      authUserRef.current = nextSession?.user || null;

      if (!nextSession?.user) {
        appUserRef.current = null;
        sessionValidated.current = true;
        setAppUser(null);
        setProfileError('');
        setLoading(false);
        return;
      }

      if (!backgroundRefresh) {
        setLoading(true);
        if (currentAuthUser?.id !== nextSession.user.id) {
          appUserRef.current = null;
          setAppUser(null);
        }
      }
      setProfileError('');
      try {
        const profile = await loadApplicationUser(nextSession.user.id);
        if (!mounted || version !== requestVersion.current) return;

        appUserRef.current = profile;
        sessionValidated.current = true;
        setAppUser(profile);
        setProfileError(
          profile.status === 'active'
            ? ''
            : 'Brukerkontoen er ikke aktiv. Kontakt administrator.'
        );
      } catch (failure) {
        if (!mounted || version !== requestVersion.current) return;
        appUserRef.current = null;
        sessionValidated.current = true;
        setAppUser(null);
        setProfileError(failure.message || 'Brukerprofilen kunne ikke verifiseres.');
      } finally {
        if (mounted && version === requestVersion.current && !backgroundRefresh) setLoading(false);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      queueMicrotask(() => {
        if (mounted) void applySession(nextSession, event);
      });
    });

    supabase.auth.getSession().then(({ data, error }) => {
      if (!mounted) return;
      if (error) {
        authUserRef.current = null;
        appUserRef.current = null;
        sessionValidated.current = true;
        setSession(null);
        setAuthUser(null);
        setAppUser(null);
        setProfileError('Innloggingsøkten kunne ikke gjenopprettes.');
        setLoading(false);
        return;
      }
      void applySession(data.session, 'INITIAL_SESSION');
    });

    return () => {
      mounted = false;
      requestVersion.current += 1;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    setLoading(true);
    setProfileError('');

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password
    });

    if (error) {
      setLoading(false);
      throw new Error(authErrorMessage(error));
    }

    if (!data.user || !data.session) {
      setLoading(false);
      throw new Error('Innloggingen fullførte ikke. Prøv igjen.');
    }

    try {
      const profile = await loadApplicationUser(data.user.id);
      if (profile.status !== 'active') {
        throw new Error('Brukerkontoen er ikke aktiv. Kontakt administrator.');
      }

      setSession(data.session);
      setAuthUser(data.user);
      authUserRef.current = data.user;
      setAppUser(profile);
      appUserRef.current = profile;
      sessionValidated.current = true;
      setProfileError('');
      setLoading(false);
      return profile;
    } catch (failure) {
      await supabase.auth.signOut();
      setSession(null);
      setAuthUser(null);
      authUserRef.current = null;
      setAppUser(null);
      appUserRef.current = null;
      sessionValidated.current = true;
      setProfileError(failure.message || 'Brukerprofilen kunne ikke verifiseres.');
      setLoading(false);
      throw failure;
    }
  };

  const refreshProfile = async () => {
    const currentUser = authUserRef.current;

    if (!currentUser?.id) {
      throw new Error('En aktiv innloggingsøkt er nødvendig.');
    }

    const version = ++requestVersion.current;
    setLoading(true);
    setProfileError('');

    try {
      const profile = await loadApplicationUser(currentUser.id);
      if (version !== requestVersion.current) {
        throw new Error('Innloggingsøkten ble endret. Last siden på nytt.');
      }

      appUserRef.current = profile;
      sessionValidated.current = true;
      setAppUser(profile);
      setProfileError(
        profile.status === 'active'
          ? ''
          : 'Brukerkontoen er ikke aktiv. Kontakt administrator.'
      );
      return profile;
    } catch (failure) {
      if (version === requestVersion.current) {
        appUserRef.current = null;
        setAppUser(null);
        setProfileError(failure.message || 'Brukerprofilen kunne ikke verifiseres.');
      }
      throw failure;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error('Kunne ikke logge ut. Prøv igjen.');

    requestVersion.current += 1;
    setSession(null);
    setAuthUser(null);
    authUserRef.current = null;
    setAppUser(null);
    appUserRef.current = null;
    sessionValidated.current = true;
    setProfileError('');
    setLoading(false);
  };

  const authorizedRole = appUser?.status === 'active' ? appUser.role : null;
  const contextValue = {
    role: authorizedRole,
    demoRole: role,
    setRole,
    session,
    authUser,
    appUser,
    loading,
    profileError,
    signIn,
    signOut,
    refreshProfile
  };

  if (!providerValueDiagnosticLogged) {
    providerValueDiagnosticLogged = true;
    console.info('[AUTH_BOOT_DIAG][UserRoleProvider]', {
      point: 'immediately-before-context-provider',
      signInIsFunction: typeof contextValue.signIn === 'function',
      contextValueIsObject: contextValue !== null && typeof contextValue === 'object',
      providerWillReceiveValue: true
    });
  }

  return (
    <UserRoleContext.Provider value={contextValue}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = () => useContext(UserRoleContext);